import {
  AbsoluteTimeRange,
  calendarClickToZonedDate,
  extractDurationFromNowMinus,
  getDateTimeFormat,
  getTimeRangeFromDurationString,
  interpretDateInTimeZone,
  isDurationString,
  isLocalTimeZone,
  isNowValue,
  isRelativeTimeValue,
  parseDurationString,
  TIME_RANGE_INVALID_ORDER_MESSAGE,
  TimeFormat,
  TimeRangeInputValue,
} from '@chronosphereio/core';
import type { RangePosition } from '@mui/x-date-pickers-pro';
import { add, isValid, setMilliseconds, sub } from 'date-fns';
import { type KeyboardEvent, type MouseEvent, useCallback, useMemo, useState } from 'react';
import { isValidTimeRange } from '../utils';
import { combineDateAndTime, computeCalendarValue, processDateRangeSelection } from './BaseInputCalendarWrapper';
import type { AroundATimeState, TimeRangeSelectionMode } from './FlexibleInputCalendar';
import {
  formatDateToString,
  isValidEndTimeValue,
  isValidStartTimeValue,
  normalizeTimeInput,
  tryParseDateString,
} from './flexible-time-utils';
import { useCalendarRangeInteraction } from './useCalendarRangeInteraction';

// --- Constants ---

/** Eventually we want to support custom durations, which will inherently change the nature
 * of the input, so this is a temporary set of options for the current immplementation
 */
export const AROUND_A_TIME_DURATION_OPTIONS = [
  { key: '15s', label: '±15 seconds' },
  { key: '1m', label: '±1 minute' },
  { key: '5m', label: '±5 minutes' },
  { key: '15m', label: '±15 minutes' },
  { key: '30m', label: '±30 minutes' },
  { key: '1h', label: '±1 hour' },
  { key: '3h', label: '±3 hours' },
  { key: '6h', label: '±6 hours' },
  { key: '12h', label: '±12 hours' },
  { key: '24h', label: '±24 hours' },
];

const AROUND_A_TIME_DURATION_KEYS = new Set(AROUND_A_TIME_DURATION_OPTIONS.map((o) => o.key));

const DEFAULT_AROUND_A_TIME_DURATION = '15s';

// --- Helpers ---

function isStartRelativeInput(trimmed: string): boolean {
  return isRelativeTimeValue(trimmed) || isDurationString(trimmed);
}

function isRecognizedAroundATimeDuration(duration: string): boolean {
  return AROUND_A_TIME_DURATION_KEYS.has(duration);
}

function flexibleValueToString(value: Date | string | undefined, timeFormat: TimeFormat, timeZone?: string): string {
  if (value === undefined) return '';
  if (typeof value === 'string') return value;
  if (value instanceof Date && isValid(value)) {
    return formatDateToString(value, timeFormat, timeZone);
  }
  return '';
}

/** Resolve a time input string to an absolute Date (e.g. the "center" of "around a time"). */
function resolveCenterDate(timeTrimmed: string, startDate: Date | null, getNow: () => Date): Date | undefined {
  if (isNowValue(timeTrimmed)) return getNow();
  if (isStartRelativeInput(timeTrimmed)) {
    const relativeDuration = extractDurationFromNowMinus(timeTrimmed) ?? timeTrimmed;
    try {
      return sub(getNow(), parseDurationString(relativeDuration));
    } catch {
      return undefined;
    }
  }
  return startDate ?? undefined;
}

/** Calculate the absolute time range for a relative start time and absolute end time. */
function getRelativeStartAbsoluteEndRange(
  startTrimmed: string,
  endDate: Date,
  getNow: () => Date
): AbsoluteTimeRange | undefined {
  const durationStr = extractDurationFromNowMinus(startTrimmed) ?? startTrimmed;
  try {
    const start = sub(getNow(), parseDurationString(durationStr));
    return isValidTimeRange({ start, end: endDate }) ? { start, end: endDate } : undefined;
  } catch {
    return undefined;
  }
}

interface UseFlexibleCalendarStateProps {
  aroundATime: AroundATimeState;
  onApply: (timeRange: TimeRangeInputValue) => void;
  setTimeRangeSelectionMode: (mode: TimeRangeSelectionMode) => void;
  timeFormat: TimeFormat;
  timeRangeSelectionMode: TimeRangeSelectionMode;
  initialTimeRange?: TimeRangeInputValue;
  maxDate?: Date;
  minDate?: Date;
  timeZone?: string;
}

/* eslint-disable @typescript-eslint/member-ordering */
interface FlexibleCalendarState {
  // Input state
  startInputValue: string;
  endInputValue: string;

  // Calendar-related
  calendarValue: { end: Date | null; start: Date | null };
  rangePosition: RangePosition;
  handleMonthChange: (newMonth: Date | null) => void;
  handleCalendarWrapperClick: (event: MouseEvent<HTMLDivElement>) => void;
  handleRangePositionChange: (newPosition: RangePosition) => void;
  handleDateRangeChange: (value: [Date | null, Date | null]) => void;
  handleSingleDateChange: (value: Date | null) => void;

  // Validation
  isValidTime: boolean;
  startHasError: boolean;
  endHasError: boolean;
  hasRangeOrderError: boolean;
  startFormatHint: string;
  endHelperText: string | undefined;

  // Handlers
  onClickApply: () => void;
  setEndTimeToNow: () => void;
  handleTimeRangeSelectionModeChange: (e: MouseEvent<HTMLElement>, newValue: TimeRangeSelectionMode) => void;
  handleStartInputChange: (value: string) => void;
  handleEndInputChange: (value: string) => void;
  handleStartKeyDown: (e: KeyboardEvent) => void;
  normalizeStartInput: () => string;
}
/* eslint-enable @typescript-eslint/member-ordering */

export function useFlexibleCalendarState({
  initialTimeRange,
  timeFormat,
  timeZone,
  aroundATime,
  timeRangeSelectionMode,
  setTimeRangeSelectionMode,
  onApply,
  minDate,
  maxDate,
}: UseFlexibleCalendarStateProps): FlexibleCalendarState {
  const {
    center: centerValue,
    duration: durationValue,
    setCenter: setAroundATimeCenter,
    setDuration: setDurationValue,
  } = aroundATime;

  // --- Input state ---

  const [startInputValue, setStartInputValue] = useState(() => {
    if (timeRangeSelectionMode === 'aroundATime' && centerValue) return centerValue;
    return normalizeTimeInput(flexibleValueToString(initialTimeRange?.start, timeFormat, timeZone));
  });
  const [endInputValue, setEndInputValue] = useState(() =>
    normalizeTimeInput(flexibleValueToString(initialTimeRange?.end, timeFormat, timeZone))
  );

  // Sync state when initialTimeRange prop changes, normalizing bare durations
  const [prevInitialTimeRange, setPrevInitialTimeRange] = useState(initialTimeRange);
  if (initialTimeRange !== prevInitialTimeRange) {
    setPrevInitialTimeRange(initialTimeRange);
    if (initialTimeRange) {
      if (timeRangeSelectionMode === 'aroundATime' && centerValue) {
        setStartInputValue(centerValue);
      } else {
        setStartInputValue(normalizeTimeInput(flexibleValueToString(initialTimeRange.start, timeFormat, timeZone)));
      }
      setEndInputValue(normalizeTimeInput(flexibleValueToString(initialTimeRange.end, timeFormat, timeZone)));
    }
  }

  // --- Derived dates ---

  const calculateDateForCal = useCallback(
    (value: string | null | undefined): Date | null => {
      if (!value) return null;
      const parsed = tryParseDateString(value, timeFormat) ?? null;
      return parsed && timeZone && !isLocalTimeZone(timeZone) ? interpretDateInTimeZone(parsed, timeZone) : parsed;
    },
    [timeFormat, timeZone]
  );
  const startDate = useMemo(() => calculateDateForCal(startInputValue), [calculateDateForCal, startInputValue]);
  const endDate = useMemo(() => calculateDateForCal(endInputValue), [calculateDateForCal, endInputValue]);

  const [isTimeModified, setIsTimeModified] = useState(false);
  const getNow = useCallback(() => setMilliseconds(new Date(), 0), []);

  const parsedInputs = useMemo(() => {
    const normalizedStart = normalizeTimeInput(startInputValue);
    const startTrimmed = normalizedStart.trim();
    const endTrimmed = endInputValue.trim();
    const startDuration = extractDurationFromNowMinus(startTrimmed);
    return {
      normalizedStart,
      startTrimmed,
      endTrimmed,
      startIsRelative: isStartRelativeInput(startTrimmed),
      endIsRelative: isRelativeTimeValue(endTrimmed),
      startDuration: startDuration !== undefined && isDurationString(startDuration) ? startDuration : undefined,
      startIsBareDuration: isDurationString(startInputValue.trim()),
      endIsNow: isNowValue(endTrimmed),
    };
  }, [startInputValue, endInputValue]);

  const calendarValue = useMemo(
    () => computeCalendarValue(startDate, endDate, startInputValue, endInputValue, timeZone),
    [startDate, endDate, startInputValue, endInputValue, timeZone]
  );

  // --- Calendar interaction ---

  const handleMonthSelect = useCallback(
    (range: { end: Date; start: Date }) => {
      const zonedStart = calendarClickToZonedDate(range.start, timeZone, false);
      const zonedEnd = calendarClickToZonedDate(range.end, timeZone, true);
      setStartInputValue(formatDateToString(zonedStart, timeFormat, timeZone));
      setEndInputValue(formatDateToString(zonedEnd, timeFormat, timeZone));
      setIsTimeModified(false);
    },
    [timeFormat, timeZone]
  );

  const {
    isSelectingNewStart,
    modifiedEndTime,
    rangePosition,
    handleMonthChange,
    handleCalendarWrapperClick,
    handleRangePositionChange,
    resetInteractionState,
  } = useCalendarRangeInteraction({
    calendarStart: calendarValue.start,
    timeZone,
    minDate,
    maxDate,
    onMonthSelect: handleMonthSelect,
  });

  // --- Validation ---

  const emptyStart = startInputValue.trim() === '';
  const emptyEnd = endInputValue.trim() === '';

  const isValidTime = useMemo(() => {
    if (timeRangeSelectionMode === 'aroundATime') {
      if (!durationValue) return false;
      const timeTrimmed = startInputValue.trim();
      if (timeTrimmed === '') return false;
      return isNowValue(timeTrimmed) || isStartRelativeInput(timeTrimmed) || startDate !== null;
    }

    const { normalizedStart, startTrimmed, endTrimmed, startIsRelative, endIsRelative, endIsNow } = parsedInputs;
    const isStartValid = isValidStartTimeValue(normalizedStart, timeFormat);
    const isEndValid = isValidEndTimeValue(endInputValue, timeFormat);

    if (!isStartValid || !isEndValid) return false;
    if (startTrimmed === '' || endTrimmed === '') return false;

    if (!startIsRelative && !endIsRelative && startDate && endDate) {
      return isValidTimeRange({ start: startDate, end: endDate });
    }
    if (startDate && endIsNow) {
      return isValidTimeRange({ start: startDate, end: getNow() });
    }
    if (isStartRelativeInput(startTrimmed) && endDate) {
      return getRelativeStartAbsoluteEndRange(startTrimmed, endDate, getNow) !== undefined;
    }
    return true;
  }, [
    parsedInputs,
    endInputValue,
    startDate,
    endDate,
    timeFormat,
    getNow,
    timeRangeSelectionMode,
    durationValue,
    startInputValue,
  ]);

  const startHasError = !emptyStart && !isValidStartTimeValue(startInputValue, timeFormat);
  const endHasError =
    timeRangeSelectionMode === 'fromTo' && !emptyEnd && !isValidEndTimeValue(endInputValue, timeFormat);
  const hasRangeOrderError =
    timeRangeSelectionMode === 'fromTo' && !startHasError && !endHasError && !emptyStart && !emptyEnd && !isValidTime;

  const dateFormat = getDateTimeFormat({ timeFormat });
  const startFormatHint = `Format: "${dateFormat}", "now-x" or "x" where x is a duration like "5m"`;
  const endFormatHint = `Format: "${dateFormat}" or "now"`;

  let endHelperText: string | undefined;
  if (endHasError) {
    endHelperText = endFormatHint;
  } else if (hasRangeOrderError) {
    endHelperText = TIME_RANGE_INVALID_ORDER_MESSAGE;
  }

  // --- Input handlers ---

  const handleStartInputChange = (value: string) => {
    setStartInputValue(value);
    if (timeRangeSelectionMode === 'aroundATime') {
      setAroundATimeCenter(value.trim());
    }
    setIsTimeModified(true);
    if (tryParseDateString(value, timeFormat)) {
      // eslint-disable-next-line react-hooks/immutability
      modifiedEndTime.current = endDate;
    }
  };

  const handleEndInputChange = (value: string) => {
    setEndInputValue(value);
    const parsed = tryParseDateString(value, timeFormat) ?? null;

    // eslint-disable-next-line react-hooks/immutability
    modifiedEndTime.current =
      parsed && timeZone && !isLocalTimeZone(timeZone) ? interpretDateInTimeZone(parsed, timeZone) : parsed;
    setIsTimeModified(true);
  };

  // --- Calendar handlers ---

  const handleDateRangeChange = (value: [Date | null, Date | null]) => {
    const wasSelectingNewStart = isSelectingNewStart.current;

    let result = processDateRangeSelection(value, {
      isTimeModified,
      existingStartTime: startDate,
      existingEndTime: modifiedEndTime.current ?? null,
      isSelectingNewStart: wasSelectingNewStart,
      timeZone,
    });

    if (wasSelectingNewStart) {
      // eslint-disable-next-line react-hooks/immutability
      isSelectingNewStart.current = false;
    }

    if (!isTimeModified) {
      if (result.start && value[0]) {
        result = { ...result, start: calendarClickToZonedDate(value[0], timeZone, false) };
      }
      if (result.end && value[1]) {
        result = { ...result, end: calendarClickToZonedDate(value[1], timeZone, true) };
      }
    }

    if (result.start) {
      setStartInputValue(formatDateToString(result.start, timeFormat, timeZone));
    }
    if (result.end) {
      setEndInputValue(formatDateToString(result.end, timeFormat, timeZone));
    } else if (wasSelectingNewStart) {
      setEndInputValue('');
    }
  };

  const handleSingleDateChange = (value: Date | null) => {
    if (!value) return;
    let zonedDate = calendarClickToZonedDate(value, timeZone, false);
    if (isTimeModified && startDate) {
      zonedDate = combineDateAndTime(value, startDate, timeZone);
    }
    const formatted = formatDateToString(zonedDate, timeFormat, timeZone);
    setStartInputValue(formatted);
    setAroundATimeCenter(formatted);
    setIsTimeModified(true);
  };

  // --- Actions ---

  const applyAndReset = () => {
    setIsTimeModified(false);
    resetInteractionState();
  };

  const onClickApply = () => {
    if (!isValidTime) return;

    if (timeRangeSelectionMode === 'aroundATime') {
      const durationParts = parseDurationString(durationValue);
      const timeTrimmed = startInputValue.trim();
      const center = resolveCenterDate(timeTrimmed, startDate, getNow);
      if (!center) return;
      setAroundATimeCenter(timeTrimmed);
      onApply({ start: sub(center, durationParts), end: add(center, durationParts) });
      applyAndReset();
      return;
    }

    const { startTrimmed, endIsNow, startDuration, startIsBareDuration } = parsedInputs;

    if ((startDuration || startIsBareDuration) && endIsNow) {
      onApply({ start: `now-${startDuration ?? startTrimmed}`, end: 'now' });
    } else if (startDate && endDate) {
      onApply({ start: startDate, end: endDate });
    } else if (startDate && endIsNow) {
      onApply({ start: startDate, end: getNow() });
    } else if ((startDuration || startIsBareDuration) && endDate) {
      const range = getRelativeStartAbsoluteEndRange(startTrimmed, endDate, getNow);
      if (!range) return;
      onApply(range);
    }

    applyAndReset();
  };

  const setEndTimeToNow = () => {
    setEndInputValue('now');
    setIsTimeModified(true);
  };

  const normalizeStartInput = () => {
    const normalized = normalizeTimeInput(startInputValue);
    setStartInputValue(normalized);
    if (isStartRelativeInput(normalized.trim()) && emptyEnd) {
      setEndInputValue('now');
    }
    return normalized;
  };

  const handleStartKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return;

    if (timeRangeSelectionMode === 'aroundATime') {
      onClickApply();
      return;
    }

    const normalized = normalizeStartInput();
    const duration = extractDurationFromNowMinus(normalized.trim()) ?? normalized.trim();
    if (isDurationString(duration)) {
      setEndInputValue('now');
      onApply(getTimeRangeFromDurationString(duration));
      applyAndReset();
    } else {
      onClickApply();
    }
  };

  // --- Mode switching ---

  const handleTimeRangeSelectionModeChange = (_: React.MouseEvent<HTMLElement>, newValue: TimeRangeSelectionMode) => {
    setTimeRangeSelectionMode(newValue);
    setIsTimeModified(false);
    // eslint-disable-next-line react-hooks/immutability
    modifiedEndTime.current = null;

    if (newValue === 'aroundATime') {
      const normalized = normalizeTimeInput(startInputValue);
      const isRelative = isStartRelativeInput(normalized.trim()) && isNowValue(endInputValue.trim());

      if (isRelative) {
        const duration = extractDurationFromNowMinus(normalized.trim()) ?? normalized.trim();
        setStartInputValue('now');
        setAroundATimeCenter('now');
        setDurationValue(isRecognizedAroundATimeDuration(duration) ? duration : DEFAULT_AROUND_A_TIME_DURATION);
      } else {
        setAroundATimeCenter(startInputValue.trim());
        setDurationValue(DEFAULT_AROUND_A_TIME_DURATION);
      }
    } else if (newValue === 'fromTo') {
      const timeTrimmed = startInputValue.trim();
      if (durationValue) {
        if (isNowValue(timeTrimmed)) {
          setStartInputValue(`now-${durationValue}`);
          setEndInputValue('now');
        } else {
          const durationParts = parseDurationString(durationValue);
          const center = resolveCenterDate(timeTrimmed, startDate, getNow);
          if (center) {
            setStartInputValue(formatDateToString(sub(center, durationParts), timeFormat, timeZone));
            setEndInputValue(formatDateToString(add(center, durationParts), timeFormat, timeZone));
          }
        }
      }
    }
  };

  return {
    startInputValue,
    endInputValue,

    calendarValue,
    rangePosition,
    handleMonthChange,
    handleCalendarWrapperClick,
    handleRangePositionChange,
    handleDateRangeChange,
    handleSingleDateChange,

    isValidTime,
    startHasError,
    endHasError,
    hasRangeOrderError,
    startFormatHint,
    endHelperText,

    onClickApply,
    setEndTimeToNow,
    handleTimeRangeSelectionModeChange,
    handleStartInputChange,
    handleEndInputChange,
    handleStartKeyDown,
    normalizeStartInput,
  };
}
